import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StillButton from './ui/StillButton';

const ShareDialog = ({ isOpen, onClose, imageUrl, starId, onDownload, shareData }) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const [mailError, setMailError] = useState(false);
    const [showEmailOptions, setShowEmailOptions] = useState(false);
    const dialogRef = useRef(null);
    const shareUrl = `${window.location.origin}/view-star/${starId}`;
    const [name, setName] = useState('');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Reset mail error when dialog opens
            setMailError(false);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleNativeShare = async () => {
        try {
            // Fetch the image and convert it to a file
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const file = new File([blob], 'star.png', { type: 'image/png' });

            // Combine the existing share data with the image file
            const shareDataWithImage = {
                ...shareData,
                files: [file]
            };

            await navigator.share(shareDataWithImage);
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Error sharing:', err);
            }
        }
    };

    const handleFacebookShare = () => {
        // Facebook doesn't support direct image sharing via URL parameters
        // But we can use Open Graph meta tags in the page head for rich previews
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    };

    const handleTwitterShare = () => {
        const text = `Still Loved`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    };

    const handleEmailShare = () => {
        //console.log('Email share handler called');
        
        if (!shareData) {
            //console.log('No shareData available');
            setMailError(true);
            setTimeout(() => setMailError(false), 3000);
            return;
        }

        try {
            const subject = `My love still shines`;
            const name = shareData?.text?.split('memory of, ')[1] || '';
            //console.log('Name extracted:', name);
            
            const body = `Today, I added a star to the Still Loved nebula in memory of, ${name}.\n\nStill Loved is a place where parents like me can honour their babies, ensuring they are always remembered. If you'd like to see my star and read our story, you can find it here: www.stillloved.co.uk/view-star/${starId}\n\nIf you know someone who has experienced early baby loss, please share this with them. Every star represents a little life still loved.\n\nAdd your own star here: www.stillloved.co.uk`;
            
            const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            //console.log('Generated mailto link:', mailtoLink);
            
            window.location.href = mailtoLink;
            
        } catch (err) {
            console.error('Email share error:', err);
            setMailError(true);
            setTimeout(() => setMailError(false), 3000);
        }
    };

    const handleWhatsAppShare = () => {
        const text = `My love still shines: ${shareUrl}`;
        // WhatsApp API doesn't support direct image sharing via URL
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const shareOptions = [
        {
            name: 'Copy a link to share your star',
            icon: <img src="/assets/images/link_icon.svg" alt="Link" />,
            action: handleCopyLink,
        },
        {
            name: 'Download your star image',
            icon: <img src="/assets/images/download_icon.svg" alt="Download" />,
            action: onDownload,
        },
        // Only show native share if shareData is available
        ...(shareData ? [{
            name: 'Share via your own social apps',
            icon: <img src="/assets/images/share_icon.svg" alt="Share" />,
            action: handleNativeShare,
        }] : []),
        /* {
            name: 'Facebook',
            icon: '📘',
            action: handleFacebookShare,
        },
        {
            name: 'Twitter',
            icon: '🐦',
            action: handleTwitterShare,
        },
        {
            name: 'Email',
            icon: '📧',
            action: handleEmailShare,
        },
        {
            name: 'WhatsApp',
            icon: '💬',
            action: handleWhatsAppShare,
        }, */
    ];

    const dialog = (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay with fade */}
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, delay: 0.25,}}
                        className="fixed inset-0 bg-black bg-opacity-90 z-[200]" 
                    />
                    
                    {/* Dialog with fade and slight scale */}
                    <div className="fixed inset-0 flex items-center justify-center z-[250]">
                        <motion.div 
                            ref={dialogRef}
                            initial={{ opacity: 0 }}
                            animate={{ 
                                opacity: 1,
                                transition: { duration: 0.5, delay: 0.5 }
                            }}
                            exit={{ 
                                opacity: 0,
                                transition: { duration: 0.2, delay: 0 }
                            }}
                            className="bg-black rounded-lg p-6 max-w-sm w-full mx-4 border border-still-blue"
                        >
                            <h2 className="text-still-white text-xl text-center font-semibold mb-4">SHARE YOUR STAR</h2>
                            
                            {/* Preview Image */}
                            {imageUrl && (
                                <div className="mb-4">
                                    <img 
                                        src={imageUrl} 
                                        alt="Star Preview" 
                                        className="w-full h-32 object-contain rounded"
                                    />
                                </div>
                            )}

                            {/* Share Options Grid */}
                            <div className="grid grid-cols-3 gap-4 mb-6">
                                {shareOptions.map((option) => (
                                    <button
                                        key={option.name}
                                        onClick={(e) => {
                                            //console.log(`Clicked ${option.name}`);
                                            option.action();
                                        }}
                                        className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-still-blue/20 transition-colors"
                                    >
                                        <span className="w-8 h-8 mb-1 flex items-center justify-center">
                                            <img src={option.icon.props.src} alt={option.icon.props.alt} className="w-6 h-6" />
                                        </span>
                                        <span className="text-still-white text-sm text-center">{option.name}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Copy Success Message */}
                            <AnimatePresence>
                                {copySuccess && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="text-still-pink text-center mb-4"
                                    >
                                        Link copied to clipboard!
                                    </motion.div>
                                )}
                                {mailError && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="text-still-pink-dark text-center mb-4"
                                    >
                                        No default mail client detected
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Close Button */}
                            <StillButton
                                onClick={onClose}
                                className="w-full"
                            >
                                CLOSE
                            </StillButton>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );

    // Render the dialog in a portal at the root level
    return createPortal(dialog, document.body);
};

export default ShareDialog; 